/**
 * `<SplitPanel>` — left-nav + right-detail layout.
 *
 * Foundations spec: design/design-system-foundations.md §9.15.
 * Replaces the prototype's `.prep-split` / `.eval-split` patterns.
 * Reusable for AI Evaluation, Interview Prep, Interview Analysis tabs
 * on the Candidate Page (30j scope).
 *
 * 30f Phase B6/B7 — additive only. No existing pages consume yet.
 */
import type { JSX, ReactNode } from "react";

interface SplitPanelNavItem {
  /** Stable identifier (matches `activeKey`). */
  key: string;
  /** Visible label. */
  label: string;
  /** Optional leading icon — node OR string (emoji / numeric badge). */
  icon?: ReactNode | string;
  /** Active-state accent color (CSS color string). */
  color?: string;
  /** Optional right-aligned count badge. */
  count?: number | string;
}

interface SplitPanelProps {
  navItems: SplitPanelNavItem[];
  /** Right-pane content for the active key. */
  detail: ReactNode;
  /** Currently selected key. */
  activeKey: string;
  /** Selection handler. */
  onSelect: (key: string) => void;
  /** Optional uppercase label above the nav. */
  navLabel?: string;
  /** Optional colored label above the detail content. */
  detailLabel?: string;
  /** CSS color for the detail label. Falls back to active item's color. */
  detailLabelColor?: string;
  /** Container height. Default '420px' per spec. */
  height?: string;
}

export function SplitPanel({
  navItems,
  detail,
  activeKey,
  onSelect,
  navLabel,
  detailLabel,
  detailLabelColor,
  height = "420px",
}: SplitPanelProps): JSX.Element {
  const activeItem = navItems.find((it) => it.key === activeKey);
  const resolvedDetailColor =
    detailLabelColor ??
    activeItem?.color ??
    "var(--accent-secondary)";

  return (
    <div
      className="flex overflow-hidden rounded-xl border border-border-default bg-surface-card"
      style={{ height }}
    >
      <nav
        className="flex w-[200px] shrink-0 flex-col overflow-hidden border-r border-border-default"
        aria-label={navLabel ?? "Split panel navigation"}
      >
        {navLabel ? (
          <div className="px-3 pt-3 pb-1 text-2xs font-bold uppercase tracking-wide text-text-tertiary">
            {navLabel}
          </div>
        ) : null}
        <ul className="flex flex-1 flex-col overflow-y-auto py-1">
          {navItems.map((it) => {
            const isActive = it.key === activeKey;
            const accent = it.color ?? "var(--accent-secondary)";
            return (
              <li key={it.key}>
                <button
                  type="button"
                  onClick={() => onSelect(it.key)}
                  className={`flex w-full cursor-pointer items-center gap-2 border-l-[3px] px-3 py-2 text-left text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-surface-card-hover text-text-primary"
                      : "border-transparent text-text-secondary hover:bg-surface-card-hover"
                  }`}
                  style={
                    isActive
                      ? ({
                          borderLeftColor: accent,
                        } as React.CSSProperties)
                      : undefined
                  }
                  aria-current={isActive ? "page" : undefined}
                >
                  {it.icon !== undefined ? (
                    <span
                      className="inline-flex shrink-0 items-center justify-center"
                      style={{ color: isActive ? accent : undefined }}
                    >
                      {it.icon}
                    </span>
                  ) : null}
                  <span className="truncate">{it.label}</span>
                  {it.count !== undefined ? (
                    <span className="ml-auto text-2xs text-text-tertiary">
                      {it.count}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <section className="flex-1 overflow-y-auto px-5 py-4">
        {detailLabel ? (
          <div
            className="mb-3 text-2xs font-bold uppercase"
            style={{ color: resolvedDetailColor, letterSpacing: "0.11em" }}
          >
            {detailLabel}
          </div>
        ) : null}
        {detail}
      </section>
    </div>
  );
}
